<template>
  <v-app-bar class="mobile_menu_bar px-3 py-0" :class="{ native_app_bar: isNativeApp }" :elevation="0" color="primary">
    <template v-slot:prepend>
      <Icon :icon="showBackIcon ? '@/assets/icons/icon-back-white.svg' : ''" size="24" color="white" :border="false" alt="Back" :disabled="!showBackIcon" @click="handleBackClick" />
      <Icon
        style="margin-top: -3px"
        :icon="!isNativeApp ? getResourceUrl(`/cdn/${sportTitle}/site/logo-icon.svg`) : ''"
        size="34"
        color="white"
        :border="false"
        :alt="`Logo for ${sportTitle} Levels`"
        to="/" />
    </template>

    <v-app-bar-title class="ma-0 mt-1 pa-0">
      {{ displayTitle }}
    </v-app-bar-title>

    <template v-slot:append>
      <UserNotifications />
      <Icon v-if="userProfileItem" :icon="profileImgUrl" size="34" color="white" :border="true" :alt="`Logo for ${sportTitle} Levels`" :to="userProfileItem.href" />
    </template>
  </v-app-bar>
  <div ref="safeAreaReader" class="safe-area-reader-hack"></div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserDataStore } from '@/store/userDataStore';
import { isMobileApp } from '@/services/mobile_app/mobileAppServices';
import { getDynamicTitle } from '@/services/admin_actions/getDynamicTitle';
import { getResourceUrl, sportTitle } from '@/services/admin_actions/apiUtils';

import Icon from '@/components/ComponentLibrary/Icon.vue';
import UserNotifications from '@/components/SingleElements/UserNotifications.vue';

const route = useRoute();
const router = useRouter();
const userDataStore = useUserDataStore();
const siteMap = computed(() => userDataStore.menuData.site_map);

/* --------------------------------------------------------- */
// Detect whether the app is running as a native mobile build or web version
const isNativeApp = computed(() => isMobileApp());

/* --------------------------------------------------------- */
// Determine which item (Profile or Log in) should appear in the top bar
// If logged in → show "Profile"
// If not logged in → show "Log in"
// Player ID is appended when available to personalize the profile link
const userProfileItem = computed(() => {
  const isLoggedIn = siteMap.value?.hasOwnProperty('Profile');
  const item = isLoggedIn ? siteMap.value['Profile'] : siteMap.value['Log in'];

  const dynamicHref = item?.href && item?.playerid ? `${item.href}?player=${item.playerid}` : item?.href || `/menu_login?action=logout&referer=${route.path}`;

  return item ? { ...item, href: dynamicHref } : { href: `/menu_login?action=logout&referer=${route.path}` };
});

/* --------------------------------------------------------- */
// Generate the correct image URL for the profile or login icon
// Falls back to a default image if none exist
const profileImgUrl = computed(() => {
  const profileImg = userDataStore.menuData.top_menu_items.Profile?.img;
  const loginImg = userDataStore.menuData.top_menu_items['Log in']?.img;
  let imgUrl = profileImg || loginImg || '/maya/icons/login-icon.png';

  // Ensure valid URL formatting (prepend "/" if relative)
  if (!imgUrl.startsWith('http') && !imgUrl.startsWith('/')) {
    imgUrl = '/' + imgUrl;
  }

  return getResourceUrl(imgUrl);
});

/* --------------------------------------------------------- */
// Determine which title should be displayed in the app bar
// Uses getDynamicTitle() for routes handled by DynamicContent.vue
// Otherwise falls back to the route name or "Dashboard"
const displayTitle = computed(() => {
  const dynamic = getDynamicTitle(route.fullPath);

  if (dynamic && dynamic !== 'Dynamic Content') {
    console.groupCollapsed('🔎 Title Resolution');
    console.log('URL:', route.fullPath);
    console.log('Source:', 'getDynamicTitle');
    console.log('Title:', dynamic);
    console.groupEnd();
    return dynamic;
  }

  if (route.name) {
    console.groupCollapsed('🔎 Title Resolution');
    console.log('URL:', route.fullPath);
    console.log('Source:', 'webRoutes (route.name)');
    console.log('Title:', route.name);
    console.groupEnd();
    return route.name as string;
  }

  console.groupCollapsed('🔎 Title Resolution');
  console.log('URL:', route.fullPath);
  console.log('Source:', 'Fallback');
  console.log('Title:', '');
  console.groupEnd();

  return '';
});

/* --------------------------------------------------------- */
// Control visibility of the back icon
// Hidden on the very first page the user lands on (no previous navigation)
// Shown only after the first route change occurs
const hasNavigated = ref(false);

// Watch route changes to track when navigation starts
watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    // When oldPath exists and differs from newPath, the user has navigated
    if (oldPath && newPath !== oldPath) hasNavigated.value = true;
  },
  { immediate: true }
);

// Only show the back icon after user has navigated away from the first page
const showBackIcon = computed(() => hasNavigated.value);

/* --------------------------------------------------------- */
// Handle back navigation safely
// Only triggers when the back icon is visible and active
const handleBackClick = () => {
  if (showBackIcon.value) router.back();
};

/* --------------------------------------------------------- */
// Calculate safe area inset for native apps to adjust app bar height
const safeAreaTop = ref(0);
const safeAreaReader = ref<HTMLElement | null>(null);
const insetTopPixels = ref(0);

onMounted(() => {
  const val = window.getComputedStyle(safeAreaReader.value!);

  // 2. Read the computed 'height' property (e.g., "54px")
  const computedHeightString = val.height;

  // 3. Convert the string "54px" to a number (54)
  safeAreaTop.value = parseFloat(computedHeightString) || 0;

  console.groupCollapsed('🧩 Safe Area Debug');
  console.log('Parsed safeAreaTop:', safeAreaTop.value);
  console.log('isNativeApp:', isNativeApp.value);
  console.log('Computed App Bar Height:', 58 + safeAreaTop.value);
  console.groupEnd();
});

const appBarHeight = computed(() => {
  return isNativeApp.value ? 58 + safeAreaTop.value : 58;
});
</script>

<style lang="scss">
// Parent class for all mobile menu bar styles
.mobile_menu_bar {
  // Ensure the content is displayed as flex
  padding-top: var(--safe-area-inset-top, 0px) !important;
  height: calc(58px + var(--safe-area-inset-top, 0px)) !important;
  .v-toolbar__content {
    height: 58px !important;
    flex-wrap: wrap;
  }

  .v-toolbar__prepend {
    width: auto;
    gap: 10px;
    margin: 0px;
  }

  .v-toolbar__append {
    width: auto;
    gap: 10px;
    margin: 0px;
  }

  .v-toolbar-title__placeholder {
    color: rgb(var(--v-theme-white));
    @include brandon_global;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0px;
  }
}

// Styles applied when not in native app mode
.native_app_bar {
  .v-toolbar__prepend {
    display: flex;
    align-content: flex-end;
    flex-wrap: wrap;
    padding-bottom: 10px !important;
  }

  .v-toolbar__content {
    display: flex;
    align-content: flex-end;
    align-items: flex-end;
    flex-wrap: wrap;
    padding-bottom: 0px !important;
    .v-toolbar-title {
      padding-bottom: 18px !important;
    }
  }

  .v-toolbar__append {
    display: flex;
    align-content: flex-end;
    flex-wrap: wrap;
    padding-bottom: 10px !important;
  }
}

.safe-area-reader-hack {
  /* Set a standard length property (like height) to the safe-area variable.
     This is where the calculation (max() evaluation) happens. */
  height: var(--safe-area-inset-top);

  /* Keep it hidden and out of the layout */
  position: absolute;
  visibility: hidden;
}
</style>
